import React, { Component, Fragment } from 'react';
import { localize } from 'react-localize-redux';
import styled from 'styled-components';
import camelCase from 'lodash/camelCase';

import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

import { MdDragHandle as DragIcon } from 'react-icons/md';

import { CSSTransitionGroup } from 'react-transition-group';

import DeleteIcon from '@material-ui/icons/DeleteOutlined';
import { createMuiTheme } from '@material-ui/core';
import { MuiThemeProvider } from '@material-ui/core';
import TextField from '@material-ui/core/TextField';
import Checkbox from '@material-ui/core/Checkbox';
import FlatButton from 'material-ui/FlatButton';

import { AccentColor } from 'helpers/AccentColorHelper';
import { colors } from 'helpers/ColorHelper';
import { getTranslatedString } from 'helpers/i18n';

import NoTasks from './NoTasks';

import { Input } from '@yuhu-inc/yuhui';
import { RenderIfToggleOn, RenderIfToggleOff } from 'components/shared/ToggleRenderer';
import CtaPrimary from 'components/ui/CtaPrimary';
import { featureEnabled } from 'helpers/ToggleBot';

// a little function to help us with reordering the result
const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

const grid = 8;

const getTaskStyle = (isDragging, draggableStyle) => ({
  // some basic styles to make the tasks look a bit nicer
  userSelect: 'none',
  padding: `0 ${grid * 2}`,
  margin: `0 0 ${grid}px 0`,
  borderRadius: '8px',

  // change background colour if dragging
  background:
    isDragging && !featureEnabled('ui.maintenance.new_tickets_ui') ? AccentColor() : 'white',

  // styles we need to apply on draggables
  ...draggableStyle,
});

const getListStyle = (isDraggingOver) => ({
  background: 'white',
  padding: `${grid * 2.5}px ${grid * 3}px ${grid}px`,
  width: '100%',
  borderRadius: '12px',
});

class TaskList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      tasks: [],
    };
    this.onDragEnd = this.onDragEnd.bind(this);
  }

  componentDidMount() {
    this.setState({ tasks: this.props.tasks });
  }

  UNSAFE_componentWillUpdate(nextProps) {
    if (nextProps.tasks !== this.props.tasks) {
      this.setState({ tasks: nextProps.tasks });
    }
  }

  onDragEnd(result) {
    // dropped outside the list
    if (!result.destination) {
      return;
    }

    this.updateTaskOrder(this.state.tasks[result.source.index], result.destination.index + 1);

    const tasks = reorder(this.state.tasks, result.source.index, result.destination.index);

    this.setState({
      tasks,
    });
  }

  addTask = () => {
    const newArray = this.state.tasks;
    newArray.push({
      id: null,
      content: '',
      complete: false,
      order_number: newArray.length + 1,
    });

    this.setState({ tasks: newArray });
  };

  deleteTask = (task) => {
    if (task.id) {
      this.props.deleteTask(task.id);
      return;
    }

    this.setState({ tasks: this.state.tasks.filter((task) => task.id) });
  };

  createOrUpdateTask = async (typedValue, originalTask) => {
    if (typedValue !== originalTask.task_name && typedValue !== '') {
      originalTask['task_name'] = typedValue;
      await this.props.onClickAction(originalTask, originalTask.id);
      this.addTask();
    }
  };

  updateTaskOrder = (task, newOrderNumber) => {
    if (newOrderNumber !== task.order_number) {
      task['order_number'] = newOrderNumber;
      this.props.onClickAction(task, task.id);
    }
  };

  markTaskAsComplete = async (isComplete, originalTask) => {
    if (isComplete !== originalTask.complete) {
      originalTask['complete'] = isComplete;
      await this.props.onClickAction(originalTask, originalTask.id);
    }
  };

  createOrUpdateOnEnterKey = (event, originalTask) => {
    if (event.charCode === 13) {
      this.createOrUpdateTask(event.target.value, originalTask);
    }
  };

  // Normally you would want to split things out into separate components.
  // But in this example everything is just done in one place for simplicity
  render() {
    const defaultTasks = this.props.tasks || [];
    const isEditable =
      !['fixed', 'closed', 'cancelled_dup', 'cancelled', 'cancelled_no_res'].includes(
        this.props.ticketStatus.toLowerCase()
      ) && !this.props.readOnly;
    const disableAddTask = this.state.tasks.length === defaultTasks.length + 1 || !isEditable;

    const noTasks = this.state.tasks.length <= 0;

    if (noTasks) {
      return <NoTasks disable={disableAddTask} editable={isEditable} click={this.addTask} />;
    }

    const displayTasks = () => (
      <DragDropContext onDragEnd={this.onDragEnd}>
        <Droppable droppableId="droppable">
          {(provided, snapshot) => (
            <div ref={provided.innerRef} style={getListStyle(snapshot.isDraggingOver)}>
              <CSSTransitionGroup
                transitionName="add_task"
                transitionEnterTimeout={500}
                transitionLeaveTimeout={500}
              >
                {this.state.tasks.map((task, index) => (
                  <Draggable
                    key={`maint-task-${task.id}-${index}`}
                    draggableId={`maint-task-${task.id}-${index}`}
                    index={index}
                    isDragDisabled={this.props.readOnly}
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={getTaskStyle(snapshot.isDragging, provided.draggableProps.style)}
                      >
                        <RenderIfToggleOff toggle="ui.maintenance.new_tickets_ui">
                          <span>
                            <DragIcon width={30} height={30} />
                          </span>
                          <MuiThemeProvider
                            theme={createMuiTheme({
                              overrides: {
                                MuiCheckbox: {
                                  colorSecondary: {
                                    '&$checked': {
                                      color: AccentColor(),
                                    },
                                  },
                                  colorPrimary: {
                                    '&$checked': {
                                      color: AccentColor(),
                                    },
                                  },
                                },
                                MuiInput: {
                                  root: {
                                    fontFamily: 'Circular',
                                    fontSize: '14px',
                                    color: '#4e4e4e',
                                  },
                                  underline: {
                                    '&:not($disabled):before': {
                                      border: 'none',
                                    },
                                    '&:not($disabled):after': {
                                      borderColor: AccentColor(),
                                    },
                                    '&:hover:not($disabled):not($focused):not($error):before': {
                                      borderColor: '#c5c5c5 !important',
                                    },
                                  },
                                },
                              },
                            })}
                          >
                            <span>
                              <Checkbox
                                value={true}
                                checked={task.complete}
                                disabled={!task.id || this.props.readOnly}
                                onClick={(event) =>
                                  this.markTaskAsComplete(event.target.checked, task)
                                }
                              />
                            </span>
                            <span>
                              <TextField
                                placeholder={getTranslatedString('landlordPortal.taskList.task')}
                                defaultValue={task.task_name}
                                onBlur={(event) =>
                                  this.createOrUpdateTask(event.target.value, task)
                                }
                                onKeyPress={(event) => this.createOrUpdateOnEnterKey(event, task)}
                                style={{
                                  width: 'calc(100% - 106px)',
                                  marginTop: '10px',
                                }}
                                required
                                disabled={this.props.readOnly || !isEditable}
                              />
                            </span>

                            {isEditable && (
                              <span>
                                <DeleteIcon
                                  style={{
                                    fontSize: '27px',
                                    verticalAlign: 'middle',
                                    color: '#757575',
                                  }}
                                  onClick={() => {
                                    if (!isEditable) {
                                      return;
                                    }
                                    this.deleteTask(task);
                                  }}
                                />
                              </span>
                            )}
                          </MuiThemeProvider>
                        </RenderIfToggleOff>

                        <RenderIfToggleOn toggle="ui.maintenance.new_tickets_ui">
                          <NewTaskListItem
                            task={task}
                            isEditable={isEditable}
                            readOnly={this.props.readOnly}
                            markTaskAsComplete={this.markTaskAsComplete}
                            createOrUpdateTask={this.createOrUpdateTask}
                            createOrUpdateOnEnterKey={this.createOrUpdateOnEnterKey}
                            deleteTask={this.deleteTask}
                          />
                        </RenderIfToggleOn>
                      </div>
                    )}
                  </Draggable>
                ))}
              </CSSTransitionGroup>
              {!isEditable && (
                <div style={{ textAlign: 'center', fontSize: '14px' }}>
                  <p>
                    {getTranslatedString('landlordPortal.taskList.warning', {
                      status: getTranslatedString(
                        `maintenanceStatus.${camelCase(this.props.ticketStatus)}`
                      ),
                    })}
                  </p>
                </div>
              )}
              <RenderIfToggleOff toggle="ui.maintenance.new_tickets_ui">
                {isEditable && (
                  <div className="row" style={{ textAlign: 'center', margin: '15px 0' }}>
                    <FlatButton
                      label={getTranslatedString('landlordPortal.taskList.addTask')}
                      type="button"
                      backgroundColor={disableAddTask ? colors.LIGHT_GRAY : AccentColor()}
                      hoverColor="grey"
                      style={{ color: 'white' }}
                      disabled={disableAddTask}
                      onClick={this.addTask}
                    />
                  </div>
                )}
              </RenderIfToggleOff>

              <RenderIfToggleOn toggle="ui.maintenance.new_tickets_ui">
                {isEditable && (
                  <div className="row" style={{ textAlign: 'center', margin: '25px 0' }}>
                    <CtaPrimary onClick={this.addTask} disabled={disableAddTask}>
                      {getTranslatedString('landlordPortal.taskList.addTask')}
                    </CtaPrimary>
                  </div>
                )}
              </RenderIfToggleOn>
            </div>
          )}
        </Droppable>
      </DragDropContext>
    );

    return displayTasks();
  }
}

export default localize()(TaskList);

const StyledNewTaskTextContainer = styled.div`
  width: calc(100% - 75px);
  display: inline-flex;
  border: 1px solid ${colors.DISABLED_GRAY};
  border-radius: 8px;
  padding: 7px;
`;

const StyledNewTaskContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 15px;
`;

const NewTaskListItem = ({
  task,
  isEditable,
  readOnly,
  markTaskAsComplete,
  createOrUpdateTask,
  createOrUpdateOnEnterKey,
  deleteTask,
}) => {
  return (
    <React.Fragment>
      <StyledNewTaskContainer>
        <span>
          <DragIcon width={30} height={30} />
        </span>
        <StyledNewTaskTextContainer>
          <Input
            placeholder={getTranslatedString('landlordPortal.taskList.task')}
            type="text"
            defaultValue={task.task_name}
            onBlur={(event) => createOrUpdateTask(event.target.value, task)}
            onKeyPress={(event) => createOrUpdateOnEnterKey(event, task)}
            style={{ border: 'none', padding: '0px 10px' }}
            disabled={readOnly || !isEditable}
          />
          <Checkbox
            value={true}
            checked={task.complete}
            disabled={!task.id || readOnly || !isEditable}
            onClick={(event) => markTaskAsComplete(event.target.checked, task)}
            style={{ borderColor: 'red', padding: '0px 10px' }}
          />
        </StyledNewTaskTextContainer>
        {isEditable && (
          <span>
            <DeleteIcon
              style={{
                fontSize: '24px',
                verticalAlign: 'middle',
                color: AccentColor(),
              }}
              onClick={() => {
                if (!isEditable) {
                  return;
                }
                deleteTask(task);
              }}
            />
          </span>
        )}
      </StyledNewTaskContainer>
    </React.Fragment>
  );
};
